import { Combobox, ComboboxInput, ComboboxOption, ComboboxOptions, Popover, PopoverButton, PopoverPanel } from '@headlessui/react'
import classNames from 'classnames'
import { ComponentType, JSX, useMemo, useState } from 'react'
import { ChevronDown } from 'react-feather'
import { useTranslation } from 'react-i18next'
import { getCountryCallingCode, type Country } from 'react-phone-number-input'

export interface CountrySelectOption {
  value?: Country
  label: string
  divider?: boolean
}

interface CountryIconProps {
  country?: Country
  label: string
  aspectRatio?: number
}

/**
 * Props passed by react-phone-number-input to a custom `countrySelectComponent`.
 */
export interface PhoneCountrySelectProps {
  value?: Country
  onChange: (value?: Country) => void
  options: CountrySelectOption[]
  iconComponent: ComponentType<CountryIconProps>
  disabled?: boolean
  readOnly?: boolean
  onFocus?: () => void
  onBlur?: () => void
  className?: string
  name?: string
  'aria-label'?: string
}

function normalizeForSearch (value: string): string {
  return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
}

/**
 * Case- and diacritic-insensitive match on localized name, ISO code and calling code (with or without `+`).
 * An empty query returns the options untouched (including dividers).
 */
export function filterCountryOptions (options: CountrySelectOption[], query: string): CountrySelectOption[] {
  const needle = normalizeForSearch(query.trim())
  if (needle === '') {
    return options
  }
  const digits = needle.replace(/^\+/, '')
  const isNumericQuery = digits !== '' && /^\d+$/.test(digits)
  return options.filter(option => {
    if (option.divider === true || option.value === undefined) {
      return false
    }
    if (normalizeForSearch(option.label).includes(needle)) {
      return true
    }
    if (option.value.toLowerCase() === needle) {
      return true
    }
    return isNumericQuery && getCountryCallingCode(option.value).startsWith(digits)
  })
}

// Headless UI Combobox needs a non-null option value for the "International" entry (country undefined)
const INTERNATIONAL = 'INTERNATIONAL'
type ComboboxValue = Country | typeof INTERNATIONAL

function toComboboxValue (country?: Country): ComboboxValue {
  return country ?? INTERNATIONAL
}

function optionKey (option: CountrySelectOption, index: number): string {
  return option.divider === true ? `divider-${index}` : toComboboxValue(option.value)
}

interface CountrySearchPanelProps {
  value?: Country
  options: CountrySelectOption[]
  iconComponent: ComponentType<CountryIconProps>
  onSelect: (value?: Country) => void
}

/**
 * Mounted only while the popover is open, so the search query resets on every open without effects.
 */
function CountrySearchPanel ({ value, options, iconComponent: Icon, onSelect }: CountrySearchPanelProps): JSX.Element {
  const { t } = useTranslation()
  const [query, setQuery] = useState('')
  const filtered = useMemo(() => filterCountryOptions(options, query), [options, query])

  return (
    <Combobox
      value={toComboboxValue(value)}
      onChange={(selected: ComboboxValue | null) => {
        // Headless emits null when the search text is emptied (e.g. Escape in the input); that is not a selection
        if (selected !== null) {
          onSelect(selected === INTERNATIONAL ? undefined : selected)
        }
      }}
      // Start in the open state: the options are always visible here, and without this the Enter key and
      // aria-expanded / aria-activedescendant only activate after an arrow key or a typed character.
      __demoMode
    >
      <ComboboxInput
        type='text'
        autoComplete='off'
        className='block w-full h-10 px-3 text-base border-0 border-b border-gray-200 rounded-t-sm focus:ring-0 focus:border-brand-primary'
        placeholder={t('field:search_country', 'Search country')}
        displayValue={() => query}
        onChange={event => setQuery(event.target.value)}
      />
      <ComboboxOptions static className='max-h-60 overflow-auto py-1 focus:outline-hidden'>
        {filtered.length === 0 && (
          <li className='px-3 py-2 text-sm text-gray-500'>{t('field:no_countries_found', 'No countries found')}</li>
        )}
        {filtered.map((option, index) => option.divider === true
          ? <li key={optionKey(option, index)} role='separator' className='my-1 border-t border-gray-200' />
          : (
              <ComboboxOption
                key={optionKey(option, index)}
                value={toComboboxValue(option.value)}
                className='flex items-center gap-3 px-3 py-2 text-sm cursor-default select-none data-focus:bg-brand-light data-focus:text-brand-primary data-selected:font-bold'
              >
                <span className='flex-none w-6'>
                  <Icon country={option.value} label={option.label} />
                </span>
                <span className='grow truncate'>{option.label}</span>
                {option.value !== undefined && (
                  <span className='flex-none text-gray-500'>+{getCountryCallingCode(option.value)}</span>
                )}
              </ComboboxOption>
            ))}
      </ComboboxOptions>
    </Combobox>
  )
}

export function PhoneCountrySelect ({
  value,
  onChange,
  options,
  iconComponent: Icon,
  disabled = false,
  readOnly = false,
  onFocus,
  onBlur,
  className,
  'aria-label': ariaLabel
}: PhoneCountrySelectProps): JSX.Element {
  const selected = options.find(option => option.divider !== true && option.value === value)
  // With an editable calling code the library adds an "International" (country-less) entry;
  // the picker should not offer it, though `value` may still be undefined while no country matches the typed code.
  const countryOptions = useMemo(
    () => options.filter(option => option.divider === true || option.value !== undefined),
    [options]
  )

  return (
    <Popover className={classNames('PhoneInputCountry relative', className)}>
      {({ open }) => (
        <>
          <PopoverButton
            type='button'
            aria-label={ariaLabel}
            disabled={disabled || readOnly}
            onFocus={onFocus}
            onBlur={onBlur}
            className='flex items-center self-stretch gap-1 px-3 leading-none rounded-l-sm bg-white focus:outline-hidden disabled:bg-gray-200 disabled:cursor-not-allowed'
          >
            <Icon country={value} label={selected?.label ?? ''} />
            <ChevronDown className={classNames('w-4 h-4 shrink-0 text-brand-primary transition-transform', { 'rotate-180': open })} />
          </PopoverButton>
          <PopoverPanel
            focus
            className='absolute top-full left-0 z-10 mt-1 w-72 max-w-[calc(100vw-2rem)] rounded-sm bg-white border border-brand-primary ring-1 ring-brand-primary/5 shadow-lg'
          >
            {({ close }) => (
              <CountrySearchPanel
                value={value}
                options={countryOptions}
                iconComponent={Icon}
                onSelect={country => {
                  // Close first: the popover restores focus to its button on close, while the
                  // library focuses the number input after a country change. The input should win.
                  close()
                  onChange(country)
                }}
              />
            )}
          </PopoverPanel>
        </>
      )}
    </Popover>
  )
}
